import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { 
  History, 
  Search, 
  Filter,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Clock,
  Car,
  QrCode,
  User,
  MessageSquare,
  Phone,
  Ticket,
  Settings,
  Edit,
  Trash2,
  Plus,
  Send,
  Eye,
  ExternalLink,
  RefreshCw,
  Download,
  Calendar,
  ChevronRight,
  Shield,
  Ban,
  FileText
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { activityLogEndpoints, api, getApiErrorMessage } from '@/lib/api';

// Operation types configuration
const operationTypes = {
  create: { label: 'Criação', icon: Plus, color: 'bg-emerald-100 text-emerald-700' },
  update: { label: 'Atualização', icon: Edit, color: 'bg-blue-100 text-blue-700' },
  delete: { label: 'Eliminação', icon: Trash2, color: 'bg-red-100 text-red-700' },
  assign: { label: 'Atribuição', icon: User, color: 'bg-purple-100 text-purple-700' },
  unassign: { label: 'Remoção', icon: Ban, color: 'bg-amber-100 text-amber-700' },
  send: { label: 'Envio', icon: Send, color: 'bg-cyan-100 text-cyan-700' },
  deactivate: { label: 'Desativação', icon: Ban, color: 'bg-slate-100 text-slate-700' },
  login: { label: 'Login', icon: User, color: 'bg-green-100 text-green-700' },
  export: { label: 'Exportação', icon: Download, color: 'bg-indigo-100 text-indigo-700' },
};

// Entity types configuration
const entityTypes = {
  vehicle: { label: 'Veículo', icon: Car, path: '/fleet' },
  tag: { label: 'Tag', icon: QrCode, path: '/tags' },
  driver: { label: 'Condutor', icon: User, path: '/users' },
  incident: { label: 'Ocorrência', icon: AlertTriangle, path: '/incidents' },
  incident_type: { label: 'Tipo de Ocorrência', icon: FileText, path: '/incident-types' },
  ticket: { label: 'Ticket', icon: Ticket, path: '/tickets' },
  alert: { label: 'Alerta', icon: MessageSquare, path: '/mass-alerts' },
  category: { label: 'Categoria', icon: Settings, path: '/categories' },
  user: { label: 'Utilizador', icon: User, path: '/users' },
  permission: { label: 'Permissão', icon: Shield, path: '/permissions' },
  charger: { label: 'Carregador', icon: Settings, path: '/chargers' },
};

// Status configuration
const statusConfig = {
  success: { label: 'Sucesso', icon: CheckCircle2, color: 'bg-emerald-100 text-emerald-700', dotColor: 'bg-emerald-500' },
  error: { label: 'Erro', icon: XCircle, color: 'bg-red-100 text-red-700', dotColor: 'bg-red-500' },
  warning: { label: 'Aviso', icon: AlertTriangle, color: 'bg-amber-100 text-amber-700', dotColor: 'bg-amber-500' },
  pending: { label: 'Pendente', icon: Clock, color: 'bg-blue-100 text-blue-700', dotColor: 'bg-blue-500' },
};

const collectionPayload = (payload, key) => {
  const source = payload?.data ?? payload;
  if (Array.isArray(source)) return source;
  if (Array.isArray(source?.data)) return source.data;
  if (Array.isArray(source?.[key])) return source[key];
  return [];
};

const normalizeOperation = (value) => {
  const normalized = String(value ?? '').toLowerCase().replace(/[-\s]/g, '_');
  const map = {
    created: 'create',
    create: 'create',
    store: 'create',
    updated: 'update',
    update: 'update',
    edited: 'update',
    deleted: 'delete',
    delete: 'delete',
    removed: 'delete',
    assign: 'assign',
    assigned: 'assign',
    unassign: 'unassign',
    unassigned: 'unassign',
    send: 'send',
    sent: 'send',
    deactivate: 'deactivate',
    deactivated: 'deactivate',
    login: 'login',
    export: 'export',
    exported: 'export',
  };
  return map[normalized] || normalized || 'update';
};

const normalizeEntity = (value) => {
  const normalized = String(value ?? '').toLowerCase().replace(/[-\s]/g, '_');
  const map = {
    resource: 'vehicle',
    vehicle: 'vehicle',
    occurrence: 'incident',
    incident: 'incident',
    occurrence_type: 'incident_type',
    incident_type: 'incident_type',
    mass_alert: 'alert',
    alert: 'alert',
    department: 'category',
    category: 'category',
    role: 'permission',
  };
  return map[normalized] || normalized || 'user';
};

const normalizeStatus = (value) => {
  const normalized = String(value ?? '').toLowerCase();
  const map = {
    0: 'pending',
    1: 'success',
    2: 'warning',
    3: 'error',
    ok: 'success',
    successful: 'success',
    completed: 'success',
    failed: 'error',
    failure: 'error',
    danger: 'error',
    warn: 'warning',
  };
  return map[normalized] || normalized || 'success';
};

const formatTimestamp = (value) => {
  if (!value) return '';
  const normalized = String(value).replace('T', ' ').replace(/\.\d+Z?$/, '');
  return normalized.endsWith('Z') ? normalized.slice(0, -1) : normalized;
};

const parseDetails = (value) => {
  if (!value) return null;
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(value);
  } catch {
    return { details: String(value) };
  }
};

const normalizeActivityLog = (source = {}, index = 0) => {
  const attributes = source.attributes && typeof source.attributes === 'object' && !Array.isArray(source.attributes)
    ? source.attributes
    : {};
  const log = { ...source, ...attributes };
  const entity = normalizeEntity(log.entity ?? log.subject_type ?? log.model_type ?? log.auditable_type ?? log.target_type);
  const user = log.user ?? log.causer ?? log.actor ?? {};
  const entitySource = log.entity_data ?? log.subject ?? log.auditable ?? log.target ?? {};
  const operation = normalizeOperation(log.operation ?? log.action ?? log.event ?? log.type);
  const timestamp = formatTimestamp(log.timestamp ?? log.created_at ?? log.date ?? log.logged_at);

  return {
    id: log.id ?? log.uuid ?? `${timestamp || 'activity'}-${index}`,
    timestamp,
    operation,
    entity,
    entityId: log.entity_id ?? log.subject_id ?? log.model_id ?? log.auditable_id ?? entitySource.id ?? null,
    entityName: log.entity_name ?? log.subject_name ?? entitySource.name ?? entitySource.title ?? entitySource.code ?? entityTypes[entity]?.label ?? '-',
    description: log.description ?? log.message ?? log.summary ?? operationTypes[operation]?.label ?? 'Operação registada',
    details: parseDetails(log.details ?? log.properties ?? log.changes ?? log.metadata),
    user: log.user_name ?? log.actor_name ?? user.name ?? user.email ?? 'Sistema',
    userRole: log.user_role ?? log.actor_role ?? user.role?.name ?? user.role ?? 'Automático',
    status: normalizeStatus(log.status ?? log.result),
    errorMessage: log.error_message ?? log.error ?? log.exception ?? null,
    path: log.path ?? log.url ?? entityTypes[entity]?.path,
  };
};

const normalizeActivityLogs = (payload) => {
  const logs = collectionPayload(payload, 'activity_logs');
  const items = logs.length ? logs : collectionPayload(payload, 'logs');
  return items.map(normalizeActivityLog);
};

export const ActivityLog = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterOperation, setFilterOperation] = useState('all');
  const [filterEntity, setFilterEntity] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDate, setFilterDate] = useState('all');
  const [selectedLog, setSelectedLog] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [activityLogs, setActivityLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadActivityLogs = useCallback(async ({ showToast = false } = {}) => {
    setLoading(true);
    setError('');

    try {
      let response;
      try {
        response = await api.get(activityLogEndpoints.list);
      } catch (requestError) {
        if (requestError?.response?.status !== 404) throw requestError;
        response = await api.get(activityLogEndpoints.legacyList);
      }

      setActivityLogs(normalizeActivityLogs(response.data));
      if (showToast) toast.success('Histórico atualizado');
    } catch (requestError) {
      setActivityLogs([]);
      setError(getApiErrorMessage(requestError, 'Nao foi possível carregar o histórico de operações.'));
      if (showToast) toast.error(getApiErrorMessage(requestError, 'Nao foi possível atualizar o histórico.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError('');

      try {
        let response;
        try {
          response = await api.get(activityLogEndpoints.list);
        } catch (requestError) {
          if (requestError?.response?.status !== 404) throw requestError;
          response = await api.get(activityLogEndpoints.legacyList);
        }

        if (!cancelled) setActivityLogs(normalizeActivityLogs(response.data));
      } catch (requestError) {
        if (!cancelled) {
          setActivityLogs([]);
          setError(getApiErrorMessage(requestError, 'Nao foi possível carregar o histórico de operações.'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  // Filter logs
  const filteredLogs = useMemo(() => activityLogs.filter(log => {
    const query = searchTerm.toLowerCase();
    const matchesSearch =
      String(log.entityName ?? '').toLowerCase().includes(query) ||
      String(log.description ?? '').toLowerCase().includes(query) ||
      String(log.user ?? '').toLowerCase().includes(query) ||
      String(log.entityId ?? '').toLowerCase().includes(query);
    const matchesOperation = filterOperation === 'all' || log.operation === filterOperation;
    const matchesEntity = filterEntity === 'all' || log.entity === filterEntity;
    const matchesStatus = filterStatus === 'all' || log.status === filterStatus;
    
    // Date filter
    let matchesDate = true;
    if (filterDate !== 'all') {
      const logDate = new Date(String(log.timestamp ?? '').replace(' ', 'T'));
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (filterDate === 'today') {
        matchesDate = logDate >= today;
      } else if (filterDate === 'week') {
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        matchesDate = logDate >= weekAgo;
      } else if (filterDate === 'month') {
        const monthAgo = new Date(today);
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        matchesDate = logDate >= monthAgo;
      }
    }
    
    return matchesSearch && matchesOperation && matchesEntity && matchesStatus && matchesDate;
  }), [activityLogs, filterDate, filterEntity, filterOperation, filterStatus, searchTerm]);

  // Navigate to entity location
  const handleNavigate = (log) => {
    if (log.path) {
      navigate(log.path);
      toast.info(`A navegar para ${entityTypes[log.entity]?.label || log.entity}...`);
    }
  };

  // Open detail dialog
  const handleViewDetails = (log) => {
    setSelectedLog(log);
    setIsDetailOpen(true);
  };

  // Stats
  const totalLogs = activityLogs.length;
  const successCount = activityLogs.filter(l => l.status === 'success').length;
  const errorCount = activityLogs.filter(l => l.status === 'error').length;
  const warningCount = activityLogs.filter(l => l.status === 'warning').length;
  const successRate = totalLogs > 0 ? ((successCount / totalLogs) * 100).toFixed(0) : '0';

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-slate-900 text-3xl font-secondary font-bold tracking-tight">Histórico de Operações</h1>
            <p className="text-slate-600 font-primary mt-1">Registo de todas as ações realizadas no sistema</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline">
              <Download size={18} className="mr-2" />
              Exportar
            </Button>
            <Button variant="outline" onClick={() => loadActivityLogs({ showToast: true })} disabled={loading}>
              <RefreshCw size={18} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
              {loading ? 'A atualizar...' : 'Atualizar'}
            </Button>
          </div>
        </div>

        {error && (
          <Card className="p-4 border-red-200 bg-red-50 text-sm text-red-700">
            {error}
          </Card>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-slate-600 text-sm font-medium">Total Operações</p>
              <History className="text-blue-600" size={20} />
            </div>
            <p className="text-2xl font-secondary font-bold text-slate-900">{totalLogs}</p>
            <p className="text-xs text-slate-500 mt-1">Últimos 30 dias</p>
          </Card>

          <Card className="p-5 border-emerald-200 bg-emerald-50/50">
            <div className="flex items-center justify-between mb-2">
              <p className="text-emerald-700 text-sm font-medium">Sucesso</p>
              <CheckCircle2 className="text-emerald-600" size={20} />
            </div>
            <p className="text-2xl font-secondary font-bold text-emerald-900">{successCount}</p>
            <p className="text-xs text-emerald-600 mt-1">{successRate}% do total</p>
          </Card>

          <Card className="p-5 border-red-200 bg-red-50/50">
            <div className="flex items-center justify-between mb-2">
              <p className="text-red-700 text-sm font-medium">Erros</p>
              <XCircle className="text-red-600" size={20} />
            </div>
            <p className="text-2xl font-secondary font-bold text-red-900">{errorCount}</p>
            <p className="text-xs text-red-600 mt-1">Requerem atenção</p>
          </Card>

          <Card className="p-5 border-amber-200 bg-amber-50/50">
            <div className="flex items-center justify-between mb-2">
              <p className="text-amber-700 text-sm font-medium">Avisos</p>
              <AlertTriangle className="text-amber-600" size={20} />
            </div>
            <p className="text-2xl font-secondary font-bold text-amber-900">{warningCount}</p>
            <p className="text-xs text-amber-600 mt-1">Para revisão</p>
          </Card>
        </div>

        {/* Filters */}
        <Card className="p-4">
          <div className="flex flex-wrap gap-3">
            <div className="flex-1 min-w-[200px] relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
              <Input 
                placeholder="Pesquisar por descrição, utilizador, ID..." 
                className="pl-9"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <Select value={filterDate} onValueChange={setFilterDate}>
              <SelectTrigger className="w-[150px]">
                <Calendar size={14} className="mr-2" />
                <SelectValue placeholder="Período" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todo o Período</SelectItem>
                <SelectItem value="today">Hoje</SelectItem>
                <SelectItem value="week">Última Semana</SelectItem>
                <SelectItem value="month">Último Mês</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filterOperation} onValueChange={setFilterOperation}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Operação" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas Operações</SelectItem>
                {Object.entries(operationTypes).map(([key, config]) => (
                  <SelectItem key={key} value={key}>{config.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filterEntity} onValueChange={setFilterEntity}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Entidade" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas Entidades</SelectItem>
                {Object.entries(entityTypes).map(([key, config]) => (
                  <SelectItem key={key} value={key}>{config.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos Estados</SelectItem>
                {Object.entries(statusConfig).map(([key, config]) => (
                  <SelectItem key={key} value={key}>{config.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </Card>

        {/* Activity Log Table */}
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[50px]">Estado</TableHead>
                <TableHead>Data/Hora</TableHead>
                <TableHead>Operação</TableHead>
                <TableHead>Entidade</TableHead>
                <TableHead>Descrição</TableHead>
                <TableHead>Utilizador</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!loading && filteredLogs.map((log) => {
                const OperationIcon = operationTypes[log.operation]?.icon || Edit;
                const EntityIcon = entityTypes[log.entity]?.icon || FileText;
                const [date = '-', time = ''] = String(log.timestamp || '').split(' ');
                
                return (
                  <TableRow 
                    key={log.id} 
                    className={`hover:bg-slate-50 cursor-pointer ${log.status === 'error' ? 'bg-red-50/50' : log.status === 'warning' ? 'bg-amber-50/50' : ''}`}
                    onClick={() => handleViewDetails(log)}
                  >
                    <TableCell>
                      <div className={`w-3 h-3 rounded-full ${statusConfig[log.status]?.dotColor || 'bg-slate-400'}`} />
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <p className="font-medium text-slate-900">{date}</p>
                        <p className="text-slate-500">{time}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={operationTypes[log.operation]?.color || 'bg-slate-100 text-slate-700'}>
                        <OperationIcon size={12} className="mr-1" />
                        {operationTypes[log.operation]?.label || log.operation}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <EntityIcon size={16} className="text-slate-500" />
                        <div>
                          <p className="font-medium text-slate-900">{log.entityName || '-'}</p>
                          {log.entityId && (
                            <p className="text-xs text-slate-500">{log.entityId}</p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <p className="text-sm text-slate-700 max-w-[250px]">{log.description}</p>
                      {log.errorMessage && (
                        <p className="text-xs text-red-600 mt-1 max-w-[250px] truncate">{log.errorMessage}</p>
                      )}
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="text-sm font-medium text-slate-900">{log.user}</p>
                        <p className="text-xs text-slate-500">{log.userRole}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); handleViewDetails(log); }}
                        >
                          <Eye size={14} />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          onClick={(e) => { e.stopPropagation(); handleNavigate(log); }}
                          title="Ir para localização"
                        >
                          <ExternalLink size={14} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          
          {loading ? (
            <div className="p-8 text-center text-slate-500">
              <RefreshCw className="mx-auto mb-3 text-slate-300 animate-spin" size={40} />
              <p>A carregar histórico...</p>
            </div>
          ) : filteredLogs.length === 0 && (
            <div className="p-8 text-center text-slate-500">
              <History className="mx-auto mb-3 text-slate-300" size={48} />
              <p>Nenhuma operação encontrada</p>
            </div>
          )}
        </Card>

        {/* Detail Dialog */}
        <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <History className="text-blue-600" size={20} />
                Detalhes da Operação
              </DialogTitle>
            </DialogHeader>

            {selectedLog && (
              <div className="space-y-4">
                {/* Status Banner */}
                {selectedLog.status === 'error' && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <XCircle className="text-red-600 mt-0.5" size={20} />
                      <div>
                        <p className="font-medium text-red-800">Erro na Operação</p>
                        <p className="text-sm text-red-700 mt-1">{selectedLog.errorMessage}</p>
                      </div>
                    </div>
                  </div>
                )}
                
                {selectedLog.status === 'warning' && (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="text-amber-600 mt-0.5" size={20} />
                      <div>
                        <p className="font-medium text-amber-800">Aviso</p>
                        <p className="text-sm text-amber-700 mt-1">{selectedLog.errorMessage}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Main Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500 mb-1">Data/Hora</p>
                    <p className="font-medium text-slate-900">{selectedLog.timestamp}</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500 mb-1">Estado</p>
                    <Badge className={statusConfig[selectedLog.status]?.color}>
                      {statusConfig[selectedLog.status]?.label}
                    </Badge>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500 mb-1">Operação</p>
                    <Badge className={operationTypes[selectedLog.operation]?.color}>
                      {operationTypes[selectedLog.operation]?.label}
                    </Badge>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500 mb-1">Entidade</p>
                    <div className="flex items-center gap-2">
                      {(() => {
                        const EntityIcon = entityTypes[selectedLog.entity]?.icon || FileText;
                        return <EntityIcon size={16} className="text-slate-500" />;
                      })()}
                      <span className="font-medium">{entityTypes[selectedLog.entity]?.label}</span>
                    </div>
                  </div>
                </div>

                {/* Entity Details */}
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-xs text-blue-600 mb-1">Entidade Afetada</p>
                  <p className="font-secondary font-bold text-blue-900 text-lg">{selectedLog.entityName}</p>
                  {selectedLog.entityId && (
                    <p className="text-sm text-blue-700">ID: {selectedLog.entityId}</p>
                  )}
                </div>

                {/* Description */}
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500 mb-1">Descrição</p>
                  <p className="text-slate-900">{selectedLog.description}</p>
                </div>

                {/* Details */}
                {selectedLog.details && (
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500 mb-2">Detalhes da Operação</p>
                    <div className="space-y-1">
                      {Object.entries(selectedLog.details).map(([key, value]) => (
                        <div key={key} className="flex items-start justify-between text-sm">
                          <span className="text-slate-600 capitalize">{key.replace(/_/g, ' ')}:</span>
                          <span className="font-medium text-slate-900 text-right max-w-[200px]">
                            {Array.isArray(value) ? value.join(', ') : String(value)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* User */}
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500 mb-1">Executado por</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center">
                      <span className="text-white font-bold text-sm">
                        {selectedLog.user === 'Sistema' ? 'SIS' : selectedLog.user.split(' ').map(n => n[0]).join('')}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{selectedLog.user}</p>
                      <p className="text-sm text-slate-600">{selectedLog.userRole}</p>
                    </div>
                  </div>
                </div>

                {/* Navigate Button */}
                <Button 
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  onClick={() => { setIsDetailOpen(false); handleNavigate(selectedLog); }}
                >
                  <ExternalLink size={16} className="mr-2" />
                  Ir para {entityTypes[selectedLog.entity]?.label || 'localização'}
                  <ChevronRight size={16} className="ml-2" />
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};
